import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import axios from 'axios';
import type { Conversation } from '@twilio/conversations';

interface BankStatementUploadInputProps {
  conversation?: Conversation;
  onSubmit?: (file: File) => void;
  isCompleted?: boolean;
}

const BankStatementUploadInput: React.FC<BankStatementUploadInputProps> = ({ conversation, onSubmit, isCompleted = false }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  const twilioApiUrl = (process.env.REACT_APP_TWILIO_API_URL || '').trim();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadedUrl('');
    setErrorMessage('');

    if (!file) {
      return;
    }

    if (file.type !== 'application/pdf') {
      setErrorMessage('File must be in PDF format.');
      setSelectedFile(null);
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setErrorMessage('File is too large. Please upload a file smaller than 20MB.');
      setSelectedFile(null);
      return;
    }
  };

  const handleUpload = async () => {
    if (isUploading || isUploadComplete) {
      return;
    }

    if (!selectedFile) {
      setErrorMessage('Please select a file before submitting.');
      return;
    }

    if (!twilioApiUrl) {
      setErrorMessage('Missing REACT_APP_TWILIO_API_URL configuration.');
      return;
    }

    if (!conversation?.sid) {
      setErrorMessage('Conversation is not available for file upload.');
      return;
    }

    setErrorMessage('');
    setIsUploading(true);

    try {
      const uploadUrl = `${twilioApiUrl}/file-upload`;

      const formData = new FormData();
      formData.append('conversation_sid', conversation.sid);
      formData.append('file', selectedFile);

      const uploadResponse = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedAttachmentUrl =
        uploadResponse?.data?.result?.attachments?.[0]?.url ||
        uploadResponse?.data?.url ||
        '';

      setUploadedUrl(uploadedAttachmentUrl || 'uploaded');
      setIsUploadComplete(true);
      onSubmit?.(selectedFile);

    } catch (error: any) {
      console.error("Upload failed:", error);
      setErrorMessage(error?.response?.data?.message || error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box className="upload-interface" sx={{ display: 'flex', flexDirection: 'column' }}>
      <>
        <input
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
          disabled={isUploading || isUploadComplete}
        />
        <Typography variant="caption" sx={{ color: '#666', mt: 0.5 }}>
          File must be in PDF format.
        </Typography>
        <Button
          className="dafabet"
          onClick={handleUpload}
          disabled={isUploading || isUploadComplete || isCompleted}
          sx={{
            borderRadius: '14px',
            padding: '0.3rem 2rem',
            margin: '1.5rem 0 0.7rem 0',
            backgroundColor: '#af0000',
            color: '#fff',
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor: '#ffe000',
              color: '#000',
            },
          }}
        >
          {isUploading ? 'Uploading...' : 'Submit'}
        </Button>
      </>

      {errorMessage ? (
        <>
          <Typography component="h3" sx={{ color: 'red' }}>
            Upload failed due to following error:
          </Typography>
          <Typography sx={{ fontWeight: 'bold', color: 'red' }}>{errorMessage}</Typography>
        </>
      ) : (
        ''
      )}

      {(uploadedUrl || isCompleted) && (
        <div>
          <Typography sx={{ fontWeight: 'bold', color: 'green' }}>You have successfully uploaded file</Typography>
          <Typography sx={{ fontWeight: 'bold', color: '#555', marginTop: '8px' }}>
            Please wait while we check your bank statement.
          </Typography>
        </div>
      )}
    </Box>
  );
};

export default BankStatementUploadInput;
